import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { Slot } from "@radix-ui/react-slot";
import { cn } from "@/shared/utils/utils";
import CustomToolTip from "../extended/ToolTip";

const buttonVariants = cva(
  "flex items-center text-sm break-words transition-all duration-500 focus-visible:ring-1 focus-visible:ring-primary disabled:cursor-not-allowed",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-pure-white hover:bg-primary/90 tracking-wide focus-visible:ring-1 focus-visible:ring-primary focus-visible:ring-offset-2",
        destructive: "bg-destructive text-pure-white hover:bg-destructive/90",
        outline:
          "bg-white text-foreground border border-border gap-2.5 hover:bg-border/30",
        secondary: "bg-white text-placeholder border border-border gap-2.5",
        ghost:
          "hover:bg-accent hover:text-accent-foreground hover:bg-bg-hover w-full gap-2.5 !px-2 !py-[10.5px]",
        link: "text-primary underline-offset-4 hover:underline",
        white:
          "bg-white rounded-full !p-0 flex items-center justify-center !size-8 xs:!size-[40px] min-[480px]:!size-[44px]",
        icon: "bg-white text-foreground border border-border gap-2 hover:bg-border/30",
        info: "w-fit block !p-0 !h-auto",
        cancel:
          "bg-background border-0 justify-center font-semibold tracking-wide rounded-10 w-full",
        whiteOutline:
          "max-w-fit gap-2 border border-border rounded-full !py-[5.5px] !px-2 min-w-[92px] justify-center text-sm hover:bg-primary hover:text-white hover:border-primary transition-all duration-500 !h-auto",
        darkWhiteOutline:
          "max-w-fit gap-2 border border-pure-white rounded-full !py-[5.5px] !px-2 min-w-[92px] justify-center text-sm hover:bg-white hover:text-foreground transition-all duration-500 !h-auto",
        menuBtn:
          "w-full flex items-center gap-2 hover:bg-bg-hover !py-0 !px-3 rounded-none duration-0",
        activeBtn: "h-[36px]! p-2! rounded-10!",
        arrowBtn: "w-auto h-auto py-0 px-2.5!",
        add: "h-8.5! border border-border px-[13px]! font-medium gap-2.5 py-[9px] hover:bg-input",
        tip: "w-auto! h-auto! p-0!",
        normalIconBtn: "[&]:w-auto [&]:p-2.5 rounded-none",
        calendarDaysBtn: "[&]:p-0 size-7 [&]:rounded-6",
      },
      size: {
        default: "h-10 px-5 py-2.5 rounded-10 tracking-wide",
        xs: "h-6 gap-1 px-2 text-xs",
        sm: "h-8 gap-1.5 px-3",
        lg: "h-10 px-6",
        icon: "size-9",
        "icon-xs": "size-3",
        "icon-sm": "size-8",
        "icon-lg": "size-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  showTooltip?: boolean;
  tooltipContent?: React.ReactNode;
  tooltipSide?: "top" | "right" | "bottom" | "left";
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      showTooltip = false,
      tooltipContent = "",
      tooltipSide = "bottom",
      title,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : "button";
    const shouldShowTooltip = showTooltip === true && (tooltipContent || title);
    const tooltipText = tooltipContent || title || "";

    const buttonElement = (
      <Comp
        ref={ref}
        className={cn(buttonVariants({ variant, size, className }))}
        data-variant={variant ?? "default"}
        title={title}
        {...props}
      />
    );

    if (shouldShowTooltip) {
      return (
        <CustomToolTip
          triggerChild={buttonElement}
          triggerContent={tooltipText}
          side={tooltipSide}
        />
      );
    }
    return buttonElement;
  },
);

Button.displayName = "Button";

export { Button, buttonVariants };
